import React, { useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Search, ShieldAlert, AlertTriangle, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";
import { InternalAdminLayout } from "./InternalAdmin";

const InternalShell = InternalAdminLayout;

/**
 * Tenant Isolation Diagnostics (Feb 2026 stabilization phase).
 *
 * Super-admin-only console for diagnosing "I logged in but landed in the
 * wrong tenant" reports. Surfaces, for any email:
 *   * every user row that matches (case-insensitive),
 *   * the company each user is bound to (id, slug, provisioning_status),
 *   * the requester's own JWT `cid` claim so the admin can reconcile what
 *     the client is carrying with what the DB says.
 *
 * Read-only. Backed by `GET /api/internal-admin/diagnostics/user-tenant-map`.
 */
export default function InternalDiagnosticsPage() {
  const [email, setEmail] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  async function search(e) {
    e?.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        "/internal-admin/diagnostics/user-tenant-map",
        { params: { email: email.trim() } },
      );
      setData(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to load diagnostics";
      toast.error(msg);
    } finally { setLoading(false); }
  }

  return (
    <InternalShell>
      <div className="space-y-5" data-testid="diagnostics-page">
        <div>
          <div className="flex items-center gap-2 text-amber-700 mb-1">
            <ShieldAlert className="w-4 h-4" />
            <span className="text-[11px] uppercase tracking-wider font-medium">
              Stabilization tooling
            </span>
          </div>
          <h1 className="font-display text-2xl font-semibold">Tenant Isolation Diagnostics</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Look up which tenant a given user account is bound to. Use this when a
            customer reports landing in the wrong dashboard after login.
          </p>
        </div>

        <Card className="p-4">
          <form onSubmit={search} className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground block mb-1.5">
                User email
              </label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="customer@example.com"
                type="email"
                autoComplete="off"
                data-testid="diagnostics-email-input"
                className="h-10"
              />
            </div>
            <Button
              type="submit"
              disabled={loading || !email.trim()}
              data-testid="diagnostics-search-btn"
            >
              <Search className="w-3.5 h-3.5 mr-1.5" />
              {loading ? "Searching…" : "Look up"}
            </Button>
          </form>
        </Card>

        {data && (
          <>
            <Card className="p-4">
              <div className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground mb-3">
                Result · {data.match_count} matching user{data.match_count === 1 ? "" : "s"} for{" "}
                <span className="text-foreground">{data.query_email}</span>
              </div>
              {data.match_count === 0 ? (
                <div className="py-6 text-sm text-muted-foreground flex items-center gap-2" data-testid="diagnostics-empty">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  No user found with that email.
                </div>
              ) : (
                <div className="space-y-3">
                  {data.match_count > 1 && (
                    <div className="px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-center gap-2">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Multiple user rows found — this is the most common cause of
                      "wrong tenant after login". Inspect each row's company binding below.
                    </div>
                  )}
                  {data.matches.map((m) => (
                    <div
                      key={m.user_id}
                      className="border rounded-lg p-3.5 bg-white"
                      data-testid="diagnostics-match-row"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-medium text-sm">{m.email}</div>
                          <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">
                            user_id={m.user_id}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                            {m.role}
                          </Badge>
                          {m.verified ? (
                            <span className="text-[10px] text-emerald-700 flex items-center gap-1">
                              <CheckCircle2 className="w-2.5 h-2.5" /> verified
                            </span>
                          ) : (
                            <span className="text-[10px] text-amber-700">unverified</span>
                          )}
                        </div>
                      </div>
                      {m.company ? (
                        <div className="mt-3 pt-3 border-t border-dashed grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                          <CopyableField label="Tenant" value={m.company.name} />
                          <CopyableField label="Slug" value={m.company.chatbot_slug} mono />
                          <CopyableField label="Tenant ID" value={m.company.id} mono />
                          <div>
                            <div className="text-[9.5px] uppercase tracking-wider text-muted-foreground">Status</div>
                            <div className="mt-0.5 font-medium text-foreground">
                              {m.company.provisioning_status}
                              {!m.company.is_enabled && (
                                <span className="ml-1 text-rose-600">(disabled)</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 pt-3 border-t border-dashed text-xs text-rose-600 flex items-center gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          User has company_id={m.company_id} but the company row is missing.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="p-4 bg-slate-50/40">
              <div className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground mb-3">
                Your session (requester) JWT claims
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
                <CopyableField label="Your email" value={data.requester.email} />
                <CopyableField label="Role" value={data.requester.role} />
                <CopyableField label="JWT cid" value={data.requester.jwt_cid || "(none — legacy token)"} mono />
                <div>
                  <div className="text-[9.5px] uppercase tracking-wider text-muted-foreground">
                    Impersonating
                  </div>
                  <div className="mt-0.5">
                    {data.requester.jwt_impersonating ? (
                      <span className="text-amber-700 font-medium">
                        YES → {data.requester.jwt_imp_company_id}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">no</span>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
    </InternalShell>
  );
}

function CopyableField({ label, value, mono = false }) {
  const v = value ?? "—";
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <button
        type="button"
        onClick={() => {
          if (!v || v === "—") return;
          navigator.clipboard?.writeText(String(v));
          toast.success("Copied");
        }}
        className={`mt-0.5 text-left text-foreground hover:text-emerald-700 inline-flex items-center gap-1 ${
          mono ? "font-mono" : "font-medium"
        }`}
      >
        <span className="truncate max-w-[180px]" title={String(v)}>{v}</span>
        {v !== "—" && <Copy className="w-2.5 h-2.5 opacity-50 shrink-0" />}
      </button>
    </div>
  );
}
